import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { useAppContext } from "providers/appProvider";
import "assets/css/site.css";
import { USER_ROLES } from "config/roles.config";

const LocationControlDropdown = ({
  dropdownClassName,
  iconClassName = "fs-8",
  onLocationChange,
  isDisabled = false, // Default to false unless explicitly disabled
}) => {
  const {
    config: { location },
    changeLocation,
    locations,
    setLocations
  } = useAppContext();

  const UserRole = localStorage.getItem("UserRole");
  const isClientRole = UserRole === USER_ROLES.CLIENT;

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        if (locations.length > 0) return;

        const customerId = localStorage.getItem("customerId");
        if (!customerId) {
          console.error("CustomerId is missing.");
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/locationRoute/all`,
          {
            params: { customerId },
            headers: {
              Authorization: process.env.REACT_APP_BasicAuthSecret
            }
          }
        );
        setLocations(response.data);

        const storedLocationId = localStorage.getItem("LocationId");
        let initialLocation = null;

        if (storedLocationId) {
          initialLocation = response.data.find(
            (loc) => loc.LocationId === storedLocationId
          );
        }

        if (!initialLocation && isClientRole && response.data.length > 0) {
          initialLocation = response.data[0];
        }

        if (initialLocation) {
          changeLocation(initialLocation);
          localStorage.setItem("LocationId", initialLocation.LocationId);
        }
      } catch (error) {
        console.error("Failed to fetch locations:", error);
      }
    };

    fetchLocations();
  }, [changeLocation, locations.length, isClientRole]);

  if (isClientRole) {
    return null;
  }

  const handleSelect = (locationId) => {
    if (isDisabled) return; // Prevent interaction if disabled

    const selectedLocation = locations.find(
      (loc) => loc.LocationId === locationId
    );
    if (selectedLocation) {
      changeLocation(selectedLocation);
      localStorage.setItem("LocationId", locationId);

      if (onLocationChange) {
        onLocationChange(locationId);
      }
    }
  };

  return (
    <Dropdown
      navbar={true}
      as="div"
      onSelect={isDisabled ? null : handleSelect} // Prevent interaction if disabled
      className={`location-control-dropdown ${dropdownClassName ?? ""} ${isDisabled ? "disabled-dropdown" : ""
        }`} // Add custom class for disabled state
    >
      <Dropdown.Toggle
        bsPrefix="toggle"
        variant="link"
        className="nav-link dropdown-toggle d-flex align-items-center pe-1"
        disabled={isDisabled} 
        data-tour="tour-location"
      >
        {/* <span className="ms-2">{location?.Name || "Select Location"}</span> */}
        {/* Show only icon on mobile, name on desktop */}
        <FontAwesomeIcon
          icon="map-marker-alt"
          className={`d-inline d-md-none ${iconClassName}`}
        />
        <span className="d-none d-md-inline ms-2">
          {location?.Name || "Select Location"}
        </span>
      </Dropdown.Toggle>

      <Dropdown.Menu className="dropdown-caret dropdown-menu-card dropdown-menu-end mt-2">
        <div className="bg-white rounded-2 py-2 dark__bg-1000">
          {locations.map((loc) => (
            <Dropdown.Item
              key={loc.LocationId}
              active={location?.LocationId === loc.LocationId}
              eventKey={loc.LocationId}
              className="link-600 fs-10 d-flex align-items-center gap-2"
              disabled={isDisabled} // Disable the dropdown item
            >
              {loc.Name}
              {location?.LocationId === loc.LocationId && (
                <FontAwesomeIcon icon="check" className="ms-auto text-600" />
              )}
            </Dropdown.Item>
          ))}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

LocationControlDropdown.propTypes = {
  iconClassName: PropTypes.string,
  dropdownClassName: PropTypes.string,
};

export default LocationControlDropdown;
